import Vue from 'vue';
import OrdersExport from '@/plugins/graphql/queries/v2/orders-export.gql';
import { ShippingMethodType } from '@/helpers/entities/enums/v2/ShippingMethodType';
import { OrderFullModel } from '@/helpers/entities/models/OrderFullModel';
import moment from 'moment';
import { capitalize, omit } from 'lodash';
import PaginatorMixin from '@/helpers/mixins/PaginatorMixin';
import { EXPORT_PER_PAGE } from '@/helpers/constants/app';
import { PlatformOrigin } from '@/helpers/entities/enums/v2/PlatformOrigin';
export default Vue.extend({
    mixins: [PaginatorMixin],
    apollo: {
        orders: {
            query: OrdersExport,
            variables() {
                return {
                    appUuid: this.appUuid,
                    status: this.status,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    exportType: this.exportType,
                    productUuid: this.productUuid,
                    first: EXPORT_PER_PAGE,
                };
            },
            update(data) {
                const nodes = data.ordersExportCsv.edges;
                this.pageInfo = data.ordersExportCsv.pageInfo;
                this.totalCount = data.ordersExportCsv.totalCount;
                return nodes.map((orderNode) => {
                    return new OrderFullModel(orderNode.node);
                });
            },
        },
    },
    props: {
        appUuid: {
            type: String,
            default: '',
        },
        status: {
            type: String,
            default: '',
        },
        startDate: {
            type: String,
            default: '',
        },
        endDate: {
            type: String,
            default: '',
        },
        exportType: {
            type: String,
            default: '',
        },
        productUuid: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            orders: [],
            doneFetching: false,
            totalCount: null,
        };
    },
    computed: {
        dataForCSV() {
            const self = this;
            if (self.orders.length >= 1) {
                return self.orders.map((order) => {
                    const productName = [];
                    const productVariance = [];
                    const qty = [];
                    const skuMatCode = [];
                    order.items.forEach((item) => {
                        productName.push(item.productName);
                        qty.push(item.quantity);
                        const matCode = item.skuMatCode || 'N/A';
                        skuMatCode.push(matCode);
                        item.itemOptions.forEach((itemOption) => {
                            productVariance.push(itemOption.optionName);
                        });
                    });
                    const splittedAddress = order.deliveryAddressLine2.split(', ');
                    const { kycDetails } = order;
                    const { deliveryTrackingId } = order.orderStatus;
                    return {
                        ...omit(order, 'kycDetails'),
                        fullName: order.customerFullName,
                        contactNumber: order.contactNumberWithCode,
                        skuMatCode,
                        productName: productName.join(' / '),
                        productVariance: productVariance.join(' / '),
                        status: capitalize(order.orderStatus.status),
                        paymentStatus: capitalize(order.orderStatus.paymentStatus),
                        paymentName: order.paymentOption.paymentName,
                        thirdPartyPaymentReferenceId: this.getPaymentTransactionReferenceID(order),
                        shippingName: order.shippingMethod ? order.shippingMethod.name : 'N/A',
                        plateNumber: this.isParkAndMunch(order) ? order.plateNumber : 'N/A',
                        carColor: this.isParkAndMunch(order) ? order.carColor : 'N/A',
                        carModel: this.isParkAndMunch(order) ? order.carModel : 'N/A',
                        landmark: this.isParkAndMunch(order) ? order.landmark : 'N/A',
                        trackingNumber: deliveryTrackingId,
                        quantity: qty.join(','),
                        humanizedConfirmedAt: this.humanizedDate(order.confirmedAt),
                        completedAtDate: this.humanizedDate(order.completedAt),
                        deliveryCity: splittedAddress[0],
                        deliveryRegion: splittedAddress[1],
                        deliveryCountry: splittedAddress[2],
                        remarks: order.remarks ? order.remarks : '',
                        cancelledDate: this.humanizedDate(order.cancelledAt),
                        completedKycAtDate: this.humanizedDate(order.completedKycAt),
                        kycApprovedAtDate: this.humanizedDate(order.kycApprovedAt),
                        orderPlacedAt: this.humanizedDate(order.createdAt),
                        kycLevel: order.kycLevel,
                        promoCode: order.promoCode,
                        salesChannel: this.humanizePlatformOrigin(order.platformOrigin),
                        // Fields from KYC Details needs to be explicitly declared to ensure
                        // All Order Object has KYC Details Keys.
                        // ...order.kycDetails will not work for CSV Exporter since exported keys
                        // should be present on every data record
                        salutation: this.getKycDetailValue(kycDetails, 'salutation'),
                        middleName: this.getKycDetailValue(kycDetails, 'middleName'),
                        birthdate: this.getKycDetailValue(kycDetails, 'birthdate'),
                        mothersMaidenName: this.getKycDetailValue(kycDetails, 'mothersMaidenName'),
                        nationality: this.getKycDetailValue(kycDetails, 'nationality'),
                        grossMonthlyIncome: this.getKycDetailValue(kycDetails, 'grossMonthlyIncome'),
                        contactPerson: this.getKycDetailValue(kycDetails, 'contactPerson'),
                        contactPersonNumber: this.getKycDetailValue(kycDetails, 'contactPersonNumber'),
                        sssNo: this.getKycDetailValue(kycDetails, 'sssNo'),
                        gsisNo: this.getKycDetailValue(kycDetails, 'gsisNo'),
                        tinNo: this.getKycDetailValue(kycDetails, 'tinNo'),
                        billMedia: this.getKycDetailValue(kycDetails, 'billMedia'),
                        billingEmail: this.getKycDetailValue(kycDetails, 'billingEmail'),
                        billingAddress: this.getKycDetailValue(kycDetails, 'billingAddress'),
                        passportIdNo: this.getKycDetailValue(kycDetails, 'passportIdNo'),
                        passportExpiry: this.getKycDetailValue(kycDetails, 'passportExpiry'),
                        employmentType: this.getKycDetailValue(kycDetails, 'employmentType'),
                    };
                });
            }
            return [];
        },
        readyForCsvExport() {
            const self = this;
            return !!self.doneFetching && !self.$apollo.queries.orders.loading;
        },
        loadingPercentage() {
            const self = this;
            if (!self.pageInfo.hasNextPage || !self.orders) {
                return null;
            }
            return Math.round((self.orders.length / self.totalOrderCount) * 100);
        },
        totalFetchCount() {
            const self = this;
            if (!self.orders) {
                return null;
            }
            return self.orders.length;
        },
        totalOrderCount() {
            const self = this;
            if (!self.orders) {
                return null;
            }
            return self.totalCount;
        },
    },
    watch: {
        orders: {
            handler() {
                const self = this;
                // Continuosly update the orders query and object if there
                // are still unfetched records
                if (self.pageInfo.hasNextPage) {
                    self.doneFetching = false;
                    self.fetchRecordsFrom(self.pageInfo.endCursor);
                    self.$apollo.queries.orders.fetchMore({
                        variables: {
                            appUuid: self.appUuid,
                            status: self.status,
                            startDate: self.startDate,
                            endDate: self.endDate,
                            exportType: self.exportType,
                            productUuid: self.productUuid,
                            first: EXPORT_PER_PAGE,
                            after: self.startCursor,
                        },
                        updateQuery: (previousOrders, { fetchMoreResult }) => {
                            const { ordersExportCsv } = previousOrders;
                            const newOrders = fetchMoreResult.ordersExportCsv;
                            const mergedOrders = [...ordersExportCsv.edges, ...newOrders.edges];
                            const { hasNextPage: newQueryHasNextPage, endCursor: newQueryEndCursor, } = newOrders.pageInfo;
                            // Update pageInfo and Orders data object
                            self.pageInfo = newOrders.pageInfo;
                            const nodes = newOrders.edges;
                            const newNodes = nodes.map((orderNode) => {
                                return new OrderFullModel(orderNode.node);
                            });
                            self.orders = [...self.orders, ...newNodes];
                            // Update pageInfo and Orders data object
                            return {
                                orders: {
                                    ...ordersExportCsv,
                                    edges: mergedOrders,
                                    pageInfo: {
                                        ...ordersExportCsv.pageInfo,
                                        hasNextPage: newQueryHasNextPage,
                                        endCursor: newQueryEndCursor,
                                    },
                                },
                            };
                        },
                    });
                }
                else {
                    self.doneFetching = true;
                }
            },
        },
    },
    methods: {
        capitalize(text) {
            if (text) {
                return text.charAt(0).toUpperCase() + text.slice(1);
            }
            return 'N/A';
        },
        humanizePlatformOrigin(origin) {
            if (origin === PlatformOrigin.GCash) {
                return 'GLife';
            }
            if (origin === PlatformOrigin.Viber) {
                return 'Viber';
            }
            if (origin === PlatformOrigin.Instagram) {
                return 'Instagram';
            }
            return 'FB Messenger';
        },
        humanizedDate(date) {
            if (date) {
                return moment(date).format('dddd, MMMM Do YYYY');
            }
            return 'N/A';
        },
        getKycDetailValue(kycDetail, key) {
            if (kycDetail) {
                return kycDetail[key];
            }
            return 'N/A';
        },
        isParkAndMunch(order) {
            if (order?.shippingMethod) {
                return order.shippingMethod.shippingType === ShippingMethodType.ParkAndMunch;
            }
            return false;
        },
        getPaymentTransactionReferenceID(order) {
            if (order?.thirdPartyPaymentReferenceId) {
                return order.thirdPartyPaymentReferenceId;
            }
            return 'N/A';
        },
    },
    render() {
        if (this.$scopedSlots.default) {
            const self = this;
            return this.$scopedSlots.default({
                orders: self.dataForCSV,
                readyForCsvExport: self.readyForCsvExport,
                loadingPercentage: self.loadingPercentage,
                totalFetchCount: self.totalFetchCount,
                totalCount: self.totalOrderCount,
            });
        }
    },
});
